import {
  ChangeDetectionStrategy, Component, ViewChild,
  ChangeDetectorRef, NgZone, OnInit, OnChanges, OnDestroy,
} from '@angular/core';
import * as _ from 'lodash';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';

import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { SearchPageService } from 'shared/search-page/search-page.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';
import { ExportManagerService } from 'shared/export-manager/export-manager.service';

import { SearchPageComponent } from 'shared/search-page/search-page.component';
import { SearchPageSimpleHeaderComponent } from 'shared/search-page/search-page-simple-header/search-page-simple-header.component';
import { ExportManagerSearchService } from 'shared/export-manager/export-manager-search.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { PreferencesService } from 'shared/preferences/preferences.service';
import { ReturnRouteService } from 'shared/return-route/return-route.service';
import { SearchModel } from 'shared/search/search.model';
import { ExportDownloadsModalComponent } from 'shared/modal/export-downloads-modal/export-downloads-modal.component';
import { ErrorModalComponent } from 'shared/modal/error-modal/error-modal.component';
import { importExportPagesData } from 'utils/import-export-pages-data';
import { isInactive } from 'shared/export-manager/export-manager-helpers';

import { ExportScheduleManagerModalComponent } from './components/export-schedule-manager-modal/export-schedule-manager-modal.component';
import { AppRoutingService } from '../app-routing.service';
import { ReceiversModalComponent } from './components/receivers-modal/receivers-modal.component';
import { columns } from './search/search-results-columns';
import { actions } from './export-manager-actions';
import {takeWhile} from 'rxjs/operators';
import {ProductPanelComponent} from '../product-panel/product-panel.component';


@Component({
  moduleId: module.id,
  selector: 'sd-export-manager',
  templateUrl: 'export-manager.component.html',
  styleUrls: ['export-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportManagerComponent extends SearchPageComponent implements OnInit, OnDestroy {

  contentSelector: string = '#export-manager-content';
  rowsHover: boolean = true;
  searchInputPlaceholder: string = 'Search for an export file by name...';
  pagesMenuData: Array<any> = [];
  incompleteDownloads: Array<string> = [];
  incompleteReceivers: Array<string> = [];
  intervalId: any = null;
  timezone: any;
  showSidedrawer: boolean = true; // use this field to show sidedrawer
  ghostValue: string = 'export-manager';
  actions: any = actions;

  @ViewChild(ErrorModalComponent, {static: false})
  declare errorModal: ErrorModalComponent;

  @ViewChild(ExportDownloadsModalComponent, {static: false})
  downloadsModal: ExportDownloadsModalComponent;

  @ViewChild(ReceiversModalComponent, {static: false}) receiversModal: ReceiversModalComponent;

  @ViewChild(ExportScheduleManagerModalComponent, {static: false})
  exportScheduleManagerModal: ExportScheduleManagerModalComponent;

  @ViewChild(SearchPageSimpleHeaderComponent, {static: false}) declare headerEl: SearchPageSimpleHeaderComponent;

  constructor(
    public searchService: ExportManagerSearchService,
    protected exportManagerService: ExportManagerService,
    protected brandService: BrandService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected appRoutingService: AppRoutingService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected returnRouteService: ReturnRouteService,
    protected location: Location,
    protected preferencesService: PreferencesService,
    protected savedSearchesService: SavedSearchesService,
    protected userService: UserService,
  ) {
    super(
      searchService,
      brandService,
      pageService,
      processService,
      scrollToService,
      appRoutingService,
      null,
      permissionsService,
      ngZone,
      ref,
      route,
      location,
      savedSearchesService,
      router,
      userService,
    );
  }

  ngOnInit() {
    this.searchResultsColumns = _.cloneDeep(columns);

    _.forEach(importExportPagesData, (data) => {
      if (this.permissionsService.hasPermission(data.allow, false)) {
        this.pagesMenuData.push(data);
      }
    });

    // @ts-ignore
    const pageData = _.find(this.pagesMenuData, {name: 'export-manager'});

    if (pageData) {
      pageData.active = true;
    }

    this.searchRanSubj
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((results: any) => {

      this.incompleteDownloads = [];
      this.incompleteReceivers = [];
      if (results.data && results.data.length) {
        _.forEach(results.data, (row: any) => {
          const incompleteDownloads = _.map(_.filter(row.download, ['completed', false]), 'file_id');
          if (incompleteDownloads.length) {
            this.incompleteDownloads.push(...incompleteDownloads);
          }

          const receivers = _.get(row, 'receivers_data');
          if (receivers && receivers.length) {
            const incompleteReceivers = _.map(_.filter(receivers, (receiver: any) => {
              return !this.isReceiverCompleted(receiver);
            }), 'id');
            if (incompleteReceivers.length) {
              this.incompleteReceivers.push(...incompleteReceivers);
            }
          }
        });
      }
    });

    // get timezone
    this.preferencesService.getPreferences()
      .subscribe((preferences: any) => {
        this.timezone = preferences.timezone;
      });

    super.ngOnInit();

    this.startCompletedRequests();
  }

  ngOnDestroy() {
    if (this.intervalId) {
      this.intervalId = clearInterval(this.intervalId);
    }
    super.ngOnDestroy();
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  // implemented
  get storedSearchModel(): SearchModel | null {
    if (_.has(this, 'user.id')) {
      return JSON.parse(localStorage.getItem(`exportManagerSearchModel.${this.brandService.getBrand()}.${this.user.id}`));
    } else {
      return null;
    }
  }

  // implemented
  get headerColor(): string {
    return 'cornflowerBlue';
  }

  get currentRoute(): string {
    return this.searchModel && this.searchModel.route || '';
  }

  get searchInputValue(): string {
    // handle it in the components
    // to get search value from header search input
    if (this.headerEl) {
      return this.headerEl.searchInputValue;
    }
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  // implemented
  storeSearchModel(searchModel: SearchModel) {
    if (_.has(this, 'user.id')) {
      localStorage.setItem(`exportManagerSearchModel.${this.brandService.getBrand()}.${this.user.id}`, JSON.stringify(searchModel));
    }
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onSearchCall() {
    if (!this.searchModel) {
      return;
    }
    this.searchModel.currentPage = 1;
    if (this.searchForm) {
      this.searchModel.searchValue = this.searchForm.searchTermValue;
    }
    this._searchModel.next(this.searchModel);
    this.searchService.toggleForm(false);
  }

  onCancelClick() {
    if (this.searchForm && this.searchModel) {
      this.searchForm.resetValue(this.searchModel.searchValue);
    }
    this.searchService.toggleForm(false);
  }

  onDownloadClick(row: any) {
    const downloads = _.get(row, 'download');
    _.forEach(downloads, (download: any) => {
      download.isInactive = this.isInactive(row, download.name);
    });
    if (this.downloadsModal) {
      this.downloadsModal.setData(downloads);
      this.downloadsModal.show();
    }
  }

  onReceiversClick(receivers: any[]) {
    if (this.receiversModal) {
      this.receiversModal.setData(receivers);
      this.receiversModal.show();
    }
  }

  onReceiverResend(receiverDataId: string) {
    if (this.receiversModal) {
      this.receiversModal.hide();
    }
    this.incompleteReceivers.push(receiverDataId);

    // set receiver status to WAITING manually
    const item: any = _.chain(this.searchService.searchResults)
    .map('receivers_data')
    .flatten()
    .find(({id: receiverDataId} as any))
    .value();

    if (item) {
      item.status = 200;
    }

    this.startCompletedRequests();

    this.ref.markForCheck();
  }

  onRightTabClick() {
    if (this.hasPermission(['admin', 'catalog-edit'])) {
        this.router.navigate(['/import-manager']);
    } else {
      if (this.errorModal) {
        this.errorModal.title = 'You don\'t have permission to access this feature. Please contact account Admin to request permission';
        this.errorModal.show();
      }
    }
  }

  onAdvancedSearchRun() {
    if (this.searchModel instanceof SearchModel) {
      this.searchModel.currentPage = 1;
    }
    super.onAdvancedSearchRun();
  }

  onPageActionSelect(event: any) {
    if (event.action === 'export-schedule-manager' && this.exportScheduleManagerModal) {
      this.exportScheduleManagerModal.show();
    }
  }

  /*******************************************
   * COMPONENT PROTECTED METHODS
   *******************************************/

   protected addDataToColumns(): void {
     const columnsWithData: Array<any> = [];
     _.each(this.searchResultsColumns, (column: any) => {
        const newColumn = _.cloneDeep(column);
        const found: any = _.find(columns, {'col': column.col} );
        if (found && found.data) {
          newColumn.data = found.data;
        }
        columnsWithData.push(newColumn);
     });
     this.searchResultsColumns = columnsWithData;
   }

   /*******************************************
    * COMPONENT PRIVATE METHODS
    *******************************************/

   private isInactive(row: any, name: string) {
     isInactive(row, name);
   }

   private isReceiverCompleted(receiver: any) {
     return receiver.status && !(receiver.status === 200 || receiver.status === 300);
   }

   private startCompletedRequests() {
     if (this.intervalId) {
      return;
    }

     this.intervalId = setInterval(() => {
       if (
         !this.incompleteDownloads.length &&
         !this.incompleteReceivers.length &&
         this.intervalId
       ) {
         this.intervalId = clearInterval(this.intervalId);
       }

       this.exportManagerService.getCompleted(
         this.brandService.getBrand(),
         this.incompleteDownloads,
         this.incompleteReceivers,
       )
         .pipe(takeWhile(() => {
           return (this.isPageActive &&
             (!!this.incompleteDownloads.length || !!this.incompleteReceivers.length)
           );
         }))
         .subscribe((results) => {
           // DOWNLOADS
           const completed = _.filter(results.files, {completed: true});
           const failed = _.filter(results.files, {has_error: true});

           // @ts-ignore
           const incompleteDownloadsSearchRes = _.filter(this.searchService.searchResults, { download: [{completed: false}]});

           _.forEach(incompleteDownloadsSearchRes, (result: any) => {
             _.forEach(result.download, (download: any) => {
               const completedItem: any = _.find(completed, {file_id: download.file_id});
               if (completedItem) {
                  result.item_count = _.get(completedItem, 'item_count');
                  result.app_count = _.get(completedItem, 'app_count');
                  result.asset_count = _.get(completedItem, 'asset_count');
                  download.completed = true;
                  download.size = _.get(completedItem, 'file_size');
                  download.has_error = _.get(completedItem, 'has_error');
                  download.has_xsd_errors = _.get(completedItem, 'has_xsd_errors');
                 _.pull(this.incompleteDownloads, download.file_id);
               } else {
                 const failedItem: any = _.find(failed, {file_id: download.file_id});
                 if (failedItem) {
                    result.item_count = _.get(failedItem, 'item_count');
                    result.app_count = _.get(failedItem, 'app_count');
                    result.asset_count = _.get(failedItem, 'asset_count');
                    download.has_error = true;
                    download.has_xsd_errors = _.get(failedItem, 'has_xsd_errors');
                    download.size = _.get(failedItem, 'file_size');
                 }
               }
             });

             // set status 2 to result if all download files are completed
             if (!_.find(result.download, {completed: false})) {
                result.status = 2;
             }
           });

           // RECEIVERS
           const completedReceivers = _.filter(results.receiver_data, (item: any) => {
             return (item.completed || item.has_error);
           });

           const incompletedReceiversRes = _.filter(
             this.searchService.searchResults,
             (item: any) => {
               return !!(
                 _.find(item.receivers_data, {status: null}) || // null
                 _.find(item.receivers_data, {status: 200}) || // waiting
                 _.find(item.receivers_data, {status: 300}) // sending
               );
             }
           );

           const receiversDataItems: any = _.chain(incompletedReceiversRes)
           .map('receivers_data')
           .flatten()
           .value();

           _.forEach(receiversDataItems, (receiver: any) => {
               const completedReceiver: any = _.find(completedReceivers, {receiver_data_id: receiver.id});

               if (completedReceiver) {
                 if (_.has(completedReceiver, 'status') && _.get(completedReceiver, 'status')) {
                  receiver.status = _.get(completedReceiver, 'status');
                 }
                 _.pull(this.incompleteReceivers, receiver.id);
               }
             }
           );
           this.ref.markForCheck();
         });
     }, 10000);
   }
}
